import { useMemo, useState } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { useTranslation } from '@/i18n';
import { Card } from '@/components/Shared/Card';
import { Button } from '@/components/Shared/Button';
import { Badge } from '@/components/Shared/Badge';
import { ImportResultView } from '@/components/Shared/ImportResultView';
import { useExcelIo } from '@/hooks/useExcelIo';
import { useServiceStore } from '@/store/useServiceStore';
import { computeMissingServices, useNetzplanImportStore } from '@/store/useNetzplanImportStore';
import { buildAufgabenFile, buildKundenFile, buildObjekteFile } from '@/pages/Import/logic/buildWorkbooks';
import type { ExcelEntity } from '@/utils/excelIo';
import type { ImportResult } from '@/types/excelImport';
import { StepFooter } from './StepFooter';

type StageId = 'services' | 'customers' | 'objects' | 'tasks' | 'done';
const ORDER: StageId[] = ['services', 'customers', 'objects', 'tasks', 'done'];

export function CommitStep() {
  const { t } = useTranslation('import');
  const [stage, setStage] = useState<StageId>('services');
  const advance = () => setStage(ORDER[Math.min(ORDER.indexOf(stage) + 1, ORDER.length - 1)]);

  // All make-hooks are called unconditionally (rules of hooks); only the active stage renders.
  const makeCustomers = useMakeCustomers();
  const makeObjects = useMakeObjects();
  const makeTasks = useMakeTasks();

  return (
    <Card>
      <div className="space-y-5">
        <p className="text-[13px] text-slate-600">{t('commit.hint')}</p>

        <ol className="flex flex-wrap gap-2">
          {ORDER.filter((s) => s !== 'done').map((s) => {
            const state = ORDER.indexOf(s) < ORDER.indexOf(stage) ? 'done' : s === stage ? 'active' : 'todo';
            return (
              <li key={s}>
                <Badge variant={state === 'done' ? 'success' : state === 'active' ? 'info' : 'default'}>
                  {t(`commit.stages.${s}`)}
                </Badge>
              </li>
            );
          })}
        </ol>

        {stage === 'services' && <ServicesStage onDone={advance} />}
        {stage === 'customers' && <ExcelStage entity="customers" onDone={advance} make={makeCustomers} />}
        {stage === 'objects' && <ExcelStage entity="objects" onDone={advance} make={makeObjects} />}
        {stage === 'tasks' && <ExcelStage entity="tasks" onDone={advance} make={makeTasks} />}
        {stage === 'done' && (
          <div className="flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-3 text-[13px] text-emerald-800">
            <CheckCircle2 className="w-5 h-5 shrink-0" strokeWidth={2} />
            <span>{t('commit.allDone')}</span>
          </div>
        )}

        <StepFooter back="tasks" />
      </div>
    </Card>
  );
}

/** Stage 0: create the services the Netzplan references but the catalogue lacks. */
function ServicesStage({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation('import');
  const taskSources = useNetzplanImportStore((s) => s.taskSources);
  const taskDecisions = useNetzplanImportStore((s) => s.taskDecisions);
  const services = useNetzplanImportStore((s) => s.live.services);
  // Compute in a memo (not in the selector) so we don't return a fresh array each render.
  const missing = useMemo(
    () => computeMissingServices(taskSources, taskDecisions, services),
    [taskSources, taskDecisions, services],
  );
  const refreshLive = useNetzplanImportStore((s) => s.refreshLive);
  const createService = useServiceStore((s) => s.createService);
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(false);

  if (missing.length === 0) {
    return (
      <div className="space-y-3">
        <p className="text-[13px] text-slate-600">{t('commit.services.none')}</p>
        <div className="flex justify-end">
          <Button onClick={onDone}>{t('actions.continue')}</Button>
        </div>
      </div>
    );
  }

  const run = async () => {
    setBusy(true);
    try {
      for (const name of missing) {
        await createService({ name, basePrice: 0, estimatedDurationMinutes: 60 });
      }
      await refreshLive();
      setCreated(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      <p className="text-[13px] text-slate-600">{t('commit.services.hint')}</p>
      <div className="flex flex-wrap gap-2">
        {missing.map((n) => (
          <Badge key={n} variant="warning">{n}</Badge>
        ))}
      </div>
      <div className="flex justify-end gap-3">
        {!created ? (
          <Button onClick={run} loading={busy}>{t('commit.services.create')}</Button>
        ) : (
          <Button onClick={onDone}>{t('actions.continue')}</Button>
        )}
      </div>
    </div>
  );
}

interface StageMaker {
  /** Cheap: number of rows that would be created (rebuilt against latest live data). */
  count: () => number;
  /** Heavy: write the xlsx File — only called on preview/commit. */
  buildFile: () => File | null;
}

/** Stages 1–3: build the entity workbook, dry-run, then commit through import/excel. */
function ExcelStage({
  entity,
  make,
  onDone,
}: {
  entity: ExcelEntity;
  make: StageMaker;
  onDone: () => void;
}) {
  const { t } = useTranslation('import');
  const { importing, previewImport, commitImport } = useExcelIo(entity);
  const refreshLive = useNetzplanImportStore((s) => s.refreshLive);
  const [preview, setPreview] = useState<ImportResult | null>(null);
  const [committed, setCommitted] = useState<ImportResult | null>(null);

  const rowCount = make.count();

  if (rowCount === 0) {
    return (
      <div className="space-y-3">
        <p className="text-[13px] text-slate-600">{t(`commit.${entity}.none`)}</p>
        <div className="flex justify-end">
          <Button onClick={onDone}>{t('actions.continue')}</Button>
        </div>
      </div>
    );
  }

  const doPreview = async () => {
    const file = make.buildFile();
    if (!file) return;
    const r = await previewImport(file);
    if (r) setPreview(r);
  };
  const doCommit = async () => {
    const file = make.buildFile();
    if (!file) return;
    const r = await commitImport(file);
    if (r) {
      setCommitted(r);
      await refreshLive();
    }
  };

  return (
    <div className="space-y-3">
      <p className="text-[13px] text-slate-600">{t(`commit.${entity}.hint`, { count: rowCount })}</p>

      {committed ? (
        <>
          <ImportResultView result={committed} failuresOnly={false} />
          <div className="flex justify-end">
            <Button onClick={onDone}>{t('actions.continue')}</Button>
          </div>
        </>
      ) : preview ? (
        <>
          <ImportResultView result={preview} />
          <div className="flex items-center justify-between gap-3">
            <span className="text-[12px] text-slate-500">
              {preview.failed > 0 ? t('commit.hasFailures', { count: preview.failed }) : t('commit.previewOk')}
            </span>
            <div className="flex gap-2">
              <Button variant="secondary" onClick={() => setPreview(null)} disabled={importing}>
                {t('actions.recheck')}
              </Button>
              <Button onClick={doCommit} loading={importing}>
                {t('commit.apply', { count: preview.created + preview.updated })}
              </Button>
            </div>
          </div>
        </>
      ) : (
        <div className="flex justify-end">
          <Button onClick={doPreview} loading={importing}>{t('commit.preview')}</Button>
        </div>
      )}
    </div>
  );
}

// make-hooks — each reads the store lazily so it rebuilds against the latest live data.
function useMakeCustomers(): StageMaker {
  const build = useNetzplanImportStore((s) => s.buildCustomerRows);
  return {
    count: () => build().length,
    buildFile: () => {
      const rows = build();
      return rows.length ? buildKundenFile(rows) : null;
    },
  };
}
function useMakeObjects(): StageMaker {
  const build = useNetzplanImportStore((s) => s.buildObjectRows);
  return {
    count: () => build().length,
    buildFile: () => {
      const rows = build();
      return rows.length ? buildObjekteFile(rows) : null;
    },
  };
}
function useMakeTasks(): StageMaker {
  const build = useNetzplanImportStore((s) => s.buildTaskRows);
  return {
    count: () => build().tasks.length,
    buildFile: () => {
      const { tasks, rules } = build();
      return tasks.length ? buildAufgabenFile(tasks, rules) : null;
    },
  };
}

export default CommitStep;
